import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import tableStyles from '../../shared/ui-components/Table/Table.module.scss';
import paginationStyles from '../../shared/ui-components/OverviewPagination/OverviewPagination.module.scss';
import LoadingSpinner from '../../shared/ui-components/LoadingSpinner/LoadingSpinner';
import { Column, SortingRule, usePagination, useSortBy, useTable } from 'react-table';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../shared/ui-components/Button/Button';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import iconEdit from '../../assets/icon-pencil.svg';
import { routes } from '../../store/Workflow.store';
import type { UserParam, User } from '../../shared/types';
import { getUser, UserSelectors } from '../../store/User.store';
import { rollenOptionsIncludingAdmin } from '../../shared/helper/select-options';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import { Order, RegistrierungsStatus } from '../../shared/types/enums';
import styles from './UserTable.module.scss';

export type EditCellProps = Readonly<{
    cell: { value: string };
}>;

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_ID = 'nachname';
const DEFAULT_SORT_DESC = false;

/** Spalten-ID für react-table → Backend-Sortierfeld (falls Backend andere Namen erwartet) */
const sortColumnToBackend = (id: string): string => {
    if (id === 'createdAt') return 'registration.createdAt';
    return id;
};

function loadUserPage(
    dispatch: ReturnType<typeof useDispatch>,
    registrierungsStatus: RegistrierungsStatus,
    pageIndex: number,
    pageSize: number,
    sortBy: Array<SortingRule<User>>,
    search: string
): void {
    const sortId = sortBy[0]?.id ?? DEFAULT_SORT_ID;
    const sortColumn = sortColumnToBackend(sortId);
    const sortOrder = sortBy[0]?.desc ?? DEFAULT_SORT_DESC ? Order.DESC : Order.ASC;
    dispatch(
        getUser(registrierungsStatus, {
            page: pageIndex + 1,
            size: pageSize,
            sortColumn,
            sortOrder,
            search: search.trim() || undefined,
        })
    );
}

const UserTable = (): ReactElement => {
    const dispatch = useDispatch();
    const users = useSelector(UserSelectors.user);
    const totalCount = useSelector(UserSelectors.totalCount);
    const isLoading = useSelector(UserSelectors.isLoading);
    const { registrierungsStatus } = useParams<UserParam>();
    const showRegistrierungen = registrierungsStatus === RegistrierungsStatus.NEU;
    const showUser = registrierungsStatus === RegistrierungsStatus.REGISTRIERT;
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [pageSizeState, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
    const [showSpinnerForPageOrSort, setShowSpinnerForPageOrSort] = useState(false);
    const [loadTriggeredBySearch, setLoadTriggeredBySearch] = useState(false);
    const prevSearchRef = useRef(debouncedSearch);

    useDocumentTitle('Benutzerübersicht');

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(searchInput), 300);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    const columns: Column[] = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'nachname',
            },
            {
                Header: 'Vorname',
                accessor: 'vorname',
            },
            {
                Header: 'Unternehmen',
                accessor: 'registration.unternehmen',
            },
            {
                Header: 'E-Mail',
                accessor: 'email',
            },
            {
                Header: (
                    <>
                        Registrierungs-
                        <br />
                        zeitpunkt
                    </>
                ),
                id: 'createdAt',
                sortType: 'datetime',
                // react-table only accepts any here
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                accessor: (row: any): Date => {
                    return new Date(row.registration?.createdAt);
                },
                Cell: ({ cell: { value } }): string => {
                    return dayjs(value).format('DD.MM.YYYY - HH:mm') + ' Uhr';
                },
            },
            ...(showUser
                ? [
                      {
                          Header: 'Rolle',
                          // react-table only accepts any here
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          accessor: (row: any): string => {
                              return (rollenOptionsIncludingAdmin.find((r) => r.value === row.rolle) || { value: '', label: '' }).label;
                          },
                      },
                  ]
                : []),
            {
                Header: 'PLZ',
                accessor: 'registration.postleitzahl',
            },
            {
                Header: 'Stadt',
                accessor: 'registration.stadt',
            },
            {
                Header: '',
                id: 'editColumn',
                // react-table only accepts any here
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                accessor: 'id',
                Cell: function editCell({ cell: { value } }: EditCellProps): ReactElement {
                    return (
                        <Link to={`${routes.benutzerdetails}/${value}`}>
                            <img src={iconEdit} className={styles._editIcon} alt="Benutzer bearbeiten" title="Benutzer bearbeiten" />
                        </Link>
                    );
                },
                disableSortBy: true,
            },
        ],
        [showUser]
    );

    const pageCount = Math.max(1, Math.ceil(totalCount / pageSizeState));
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        state,
        canPreviousPage,
        canNextPage,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
    } = useTable(
        {
            columns,
            data: users,
            initialState: {
                sortBy: [{ id: DEFAULT_SORT_ID, desc: DEFAULT_SORT_DESC }],
                pageSize: pageSizeState,
                pageIndex: 0,
            },
            manualPagination: true,
            manualSortBy: true,
            pageCount,
            disableSortRemove: true,
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        const triggeredBySearchOnly = prevSearchRef.current !== debouncedSearch;
        prevSearchRef.current = debouncedSearch;
        setLoadTriggeredBySearch(triggeredBySearchOnly);
        if (!triggeredBySearchOnly) setShowSpinnerForPageOrSort(true);
        loadUserPage(
            dispatch,
            registrierungsStatus,
            state.pageIndex,
            state.pageSize,
            state.sortBy as Array<SortingRule<User>>,
            debouncedSearch
        );
    }, [dispatch, registrierungsStatus, state.pageIndex, state.pageSize, state.sortBy, debouncedSearch]);

    useEffect(() => {
        if (!isLoading) setShowSpinnerForPageOrSort(false);
    }, [isLoading]);

    useEffect(() => {
        if (debouncedSearch !== '') gotoPage(0);
    }, [debouncedSearch, gotoPage]);

    const showFullSpinner = isLoading && !loadTriggeredBySearch && (users.length === 0 || showSpinnerForPageOrSort);
    return (
        <LoadingSpinner isLoading={showFullSpinner}>
            <>
                {!showFullSpinner && (
                    <div className={styles._headerContainer}>
                        <h2>{showRegistrierungen ? 'Offene Registrierungen' : 'Registrierte Benutzer'}</h2>
                        <div className={styles._searchField__container}>
                            <strong>Suche: </strong>
                            <input
                                type="text"
                                value={searchInput}
                                onChange={(e): void => setSearchInput(e.target.value)}
                                onKeyDown={(e): void => {
                                    if (e.key === 'Enter') e.preventDefault();
                                }}
                                placeholder={`in ${totalCount} Benutzern`}
                                className={styles._searchField}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                )}
                <div className={[tableStyles._tableContainer, tableStyles['_tableContainer--scrolling']].join(' ')}>
                    <table {...getTableProps()} className={tableStyles._table}>
                        <thead>
                            {headerGroups.map((headerGroup, index) => (
                                <tr
                                    {...headerGroup.getHeaderGroupProps()}
                                    className={[
                                        tableStyles._tableHeaderRow,
                                        tableStyles['_tableHeaderRow--notSticky'],
                                        tableStyles['_tableHeaderRow--nowrap'],
                                    ].join(' ')}
                                    key={index}
                                >
                                    {headerGroup.headers.map((column, i) => (
                                        <th
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            className={tableStyles['_tableCell--text']}
                                            key={i}
                                        >
                                            {column.render('Header')}
                                            <div className={tableStyles._tableSortElement}>
                                                {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : ''}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                            {page.map((row) => {
                                prepareRow(row);
                                return (
                                    <tr {...row.getRowProps()} className={tableStyles._tableBodyRow} key={row.index}>
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <td
                                                    {...cell.getCellProps()}
                                                    className={[tableStyles._tableCell, tableStyles['_tableCell--text']].join(' ')}
                                                    key={index}
                                                >
                                                    {cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className={paginationStyles._paginationContainer}>
                        <span>zu Seite:</span>
                        <div className={paginationStyles._pageJump}>
                            <input
                                type="number"
                                defaultValue={state.pageIndex + 1}
                                onChange={(event): void => {
                                    const page = event.target.value ? Number(event.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                className={paginationStyles._pageJump__input}
                            />
                        </div>{' '}
                        <span>Zeilen anzeigen:</span>
                        <select
                            value={state.pageSize}
                            onChange={(e): void => {
                                const size = Number(e.target.value);
                                setPageSize(size);
                                setPageSizeState(size);
                            }}
                            className={paginationStyles._rowAmount}
                        >
                            {[10, 20, 30, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                        <Button onClick={(): void => gotoPage(0)} disabled={!canPreviousPage} isSmall={true}>
                            {'<<'}
                        </Button>
                        <Button onClick={(): void => previousPage()} disabled={!canPreviousPage} isSmall={true}>
                            {'<'}
                        </Button>
                        <span className={paginationStyles._pageCounter}>
                            Seite&nbsp;
                            <strong>
                                {state.pageIndex + 1} von {pageCount}
                            </strong>
                        </span>
                        <Button onClick={(): void => nextPage()} disabled={!canNextPage} isSmall={true}>
                            {'>'}
                        </Button>
                        <Button onClick={(): void => gotoPage(pageCount - 1)} disabled={!canNextPage} isSmall={true}>
                            {'>>'}
                        </Button>
                    </div>
                </div>
            </>
        </LoadingSpinner>
    );
};

export default UserTable;
